import React__default, { useEffect, useRef } from 'react';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { Note } from '../../../atoms/Note.js';
import { PricingModel } from '../../../../types/servicesProviders/ServicesProviderPricing.js';
import { DropdownFormField } from './DropdownFormField.js';
import { TextFormField } from './TextFormField.js';
import './PricingFormField.scss';

/**
 * PricingFormField - A complex form field component for pricing configuration.
 *
 * This component uses useFieldArray to manage an array of pricing items.
 * It renders a dropdown for selecting a pricing model, and conditionally
 * displays the appropriate input fields based on the selected model.
 *
 * Supported models:
 * - FLAT_FEE: Requires `flatFee` (number)
 * - FIXED: Requires `fixedPercentageFee` (number, 0-1)
 * - LINEAR: Requires `pricePerMile` (number)
 * - LINEAR_HOURLY: Requires `pricePerHour` (number)
 * - STATE_BASED_FEE: Requires `defaultAmount` (number) and optional `stateData`
 * - STEPPED: Requires optional `baseDistance`, `basePrice`, and `pricePerMileAfter` (all numbers)
 *
 * @example
 * ```tsx
 * const schema = z.object({
 *   pricing: z.array(z.object({
 *     model: z.nativeEnum(PricingModel),
 *     modelDetails: z.object({
 *       flatFee: z.number().optional(),
 *       fixedPercentageFee: z.number().optional(),
 *       // ... other fields
 *     }),
 *   })),
 * });
 *
 * <Form schema={schema} onSubmit={handleSubmit}>
 *   <PricingFormField
 *     schemaField="pricing"
 *     supportedModels={[PricingModel.FLAT_FEE, PricingModel.FIXED]}
 *   />
 * </Form>
 * ```
 */
function PricingFormField({ schemaField, supportedModels, modelLabelText = 'Pricing Model', modelDescriptionText, }) {
    const { control, setValue } = useFormContext();
    // Use useFieldArray to manage the pricing array
    const { fields, append } = useFieldArray({
        control,
        name: schemaField,
    });
    // Ensure at least one item exists in the array
    useEffect(() => {
        if (fields.length === 0) {
            append({
                model: supportedModels[0] || PricingModel.FLAT_FEE,
                modelDetails: {},
            });
        }
    }, [fields.length, append, supportedModels]);
    // Watch the model field of the first item to conditionally render modelDetails fields
    const model = useWatch({
        control,
        name: `${schemaField}.0.model`,
    });
    // Track previous model to only reset on actual changes
    const previousModelRef = useRef(model);
    // Get model display names
    const modelDisplayNames = {
        [PricingModel.TIERED]: 'Tiered',
        [PricingModel.FLAT_FEE]: 'Flat Fee',
        [PricingModel.FIXED]: 'Fixed Percentage',
        [PricingModel.LINEAR]: 'Linear',
        [PricingModel.LINEAR_HOURLY]: 'Linear Hourly',
        [PricingModel.STEPPED]: 'Stepped',
        [PricingModel.VEHICLE_TYPE_CC]: 'Vehicle Type CC',
        [PricingModel.STATE_BASED_FEE]: 'State Based Fee',
        [PricingModel.STAR_EV_DELIVERY_ESTIMATE]: 'Star EV Delivery Estimate',
        [PricingModel.EKHO_PROCESSING_FEE]: 'Ekho Processing Fee',
    };
    // Create dropdown options from supported models
    const modelOptions = supportedModels.map((modelValue) => ({
        id: modelValue,
        label: modelDisplayNames[modelValue] || modelValue,
    }));
    // Reset modelDetails when model changes (but not on initial mount)
    useEffect(() => {
        const previousModel = previousModelRef.current;
        if (model && previousModel !== undefined && model !== previousModel) {
            // Reset modelDetails to empty object when model changes
            // This ensures old modelDetails don't persist when switching models
            setValue(`${schemaField}.0.modelDetails`, {}, { shouldDirty: false });
        }
        previousModelRef.current = model;
    }, [model, schemaField, setValue]);
    // Render fields for the first item (currently only supporting single item)
    if (fields.length === 0) {
        return null;
    }
    const firstItemIndex = 0;
    const baseFieldName = `${schemaField}.${firstItemIndex}`;
    return (React__default.createElement("div", { className: 'pricing-container--rev' },
        React__default.createElement("div", { className: 'pricing-model-selector--rev' },
            React__default.createElement(DropdownFormField, { schemaField: `${baseFieldName}.model`, labelText: modelLabelText, descriptionText: modelDescriptionText, required: true, options: modelOptions, placeholder: 'Select a pricing model', buttonVariant: 'outline', size: 'large' })),
        React__default.createElement("div", { className: 'pricing-details--rev' },
            model === PricingModel.FLAT_FEE && (React__default.createElement(TextFormField, { schemaField: `${baseFieldName}.modelDetails.flatFee`, labelText: 'Flat Fee ($)', descriptionText: 'Fixed price amount', required: true, variant: 'outline', size: 'medium', placeholder: '0.00', type: 'number' })),
            model === PricingModel.FIXED && (React__default.createElement(TextFormField, { schemaField: `${baseFieldName}.modelDetails.fixedPercentageFee`, labelText: 'Fixed Percentage Fee', descriptionText: 'Percentage as a decimal (e.g., 0.05 for 5%)', required: true, variant: 'outline', size: 'medium', placeholder: '0.05', type: 'number' })),
            model === PricingModel.LINEAR && (React__default.createElement(TextFormField, { schemaField: `${baseFieldName}.modelDetails.pricePerMile`, labelText: 'Price Per Mile ($)', descriptionText: 'Price charged per mile', required: true, variant: 'outline', size: 'medium', placeholder: '0.00', type: 'number' })),
            model === PricingModel.LINEAR_HOURLY && (React__default.createElement(TextFormField, { schemaField: `${baseFieldName}.modelDetails.pricePerHour`, labelText: 'Price Per Hour ($)', descriptionText: 'Price charged per hour', required: true, variant: 'outline', size: 'medium', placeholder: '0.00', type: 'number' })),
            model === PricingModel.STATE_BASED_FEE && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Note, null, "Coming Soon: This configuration is currently managed by the Ekho team. Please reach out to your Ekho representative if you need to configure this."))),
            model === PricingModel.VEHICLE_TYPE_CC && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Note, null, "Coming Soon: This configuration is currently managed by the Ekho team. Please reach out to your Ekho representative if you need to configure this."))),
            model === PricingModel.STEPPED && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(TextFormField, { schemaField: `${baseFieldName}.modelDetails.baseDistance`, labelText: 'Base Distance (miles)', descriptionText: 'Distance included in base price (optional)', variant: 'outline', size: 'medium', placeholder: '0', type: 'number' }),
                React__default.createElement(TextFormField, { schemaField: `${baseFieldName}.modelDetails.basePrice`, labelText: 'Base Price ($)', descriptionText: 'Price for base distance (optional)', variant: 'outline', size: 'medium', placeholder: '0.00', type: 'number' }),
                React__default.createElement(TextFormField, { schemaField: `${baseFieldName}.modelDetails.pricePerMileAfter`, labelText: 'Price Per Mile After Base ($)', descriptionText: 'Price per mile after base distance (optional)', variant: 'outline', size: 'medium', placeholder: '0.00', type: 'number' }))))));
}

export { PricingFormField };
//# sourceMappingURL=PricingFormField.js.map
